import { ICallerIdService } from '../interfaces/ICallerIdService';
import { CallerIdLineType, ICallerIdResponse, ILineSelectionInfo } from '../types';
import { getMultiLineLabel } from '../../../packages/widgets/src/utils/handleMultiLineLabel';

// Simulate network delay (ms)
const MOCK_DELAY = 600;

/**
 * Mock CallerIdService for dev/testing — no real API calls.
 *
 * Usage (browser console):
 *   window.USE_MOCK_LINE_SELECTION = true;   // then refresh
 *
 * Configuration (optional, browser console):
 *   window.MOCK_LINE_SELECTION_FAIL_GET = true;   // simulate GET failure (triggers fallback)
 *   window.MOCK_LINE_SELECTION_FAIL_PUT = true;   // simulate PUT failure (triggers rollback)
 *
 * L1 has multiple caller IDs to test the hierarchical dropdown (see Constraints.png right panel).
 */
export class MockCallerIdService implements ICallerIdService {

  private selectedIndex = 0;
  private selectedCallerIdIndex: number | null = 1; // Second caller ID of L1 selected by default

  private readonly mockLines: ILineSelectionInfo[] = [
    {
      lineIndex: 0,
      lineLabel: getMultiLineLabel(1),
      title: 'Sonali Pritchard',
      number: '+1 234 456 7890',
      lineType: CallerIdLineType.DEFAULT_CLID,
      isSelected: true,
      callerIds: [
        { callerIdIndex: 0, name: 'Sonali Pritchard', number: '+1 234 456 7890', isSelected: false },
        { callerIdIndex: 1, name: 'Sales Outbound', number: '+1 234 456 7891', isSelected: true },
      ],
    },
    {
      lineIndex: 1,
      lineLabel: getMultiLineLabel(2),
      title: 'EastCoast Sales',
      number: '+1 800 111 7890',
      lineType: CallerIdLineType.CALL_CENTER_DNIS,
      isSelected: false,
      serviceUserId: 'cq-demo@example.com',
      dnisName: 'Sales Queue DNIS',
    },
    {
      lineIndex: 2,
      lineLabel: getMultiLineLabel(3),
      title: 'Midwest Sales',
      number: '+1 800 222 7890',
      lineType: CallerIdLineType.ADDITIONAL_NUMBER,
      isSelected: false,
    },
    {
      lineIndex: 3,
      lineLabel: getMultiLineLabel(4),
      title: 'West Coast Support',
      number: '+1 800 333 7890',
      lineType: CallerIdLineType.HUNT_GROUP,
      isSelected: false,
      serviceUserId: 'hg-west@example.com',
    },
    {
      lineIndex: 4,
      lineLabel: getMultiLineLabel(5),
      title: 'Partner Line',
      number: '+1 800 444 7890',
      lineType: CallerIdLineType.ADDITIONAL_NUMBER,
      isSelected: false,
    },
    {
      lineIndex: 5,
      lineLabel: getMultiLineLabel(6),
      title: 'Executive Office',
      number: '+1 800 555 7890',
      lineType: CallerIdLineType.DEFAULT_CLID,
      isSelected: false,
    },
    {
      lineIndex: 6,
      lineLabel: getMultiLineLabel(7),
      title: 'After Hours',
      number: '+1 800 666 7890',
      lineType: CallerIdLineType.CALL_CENTER_DNIS,
      isSelected: false,
      serviceUserId: 'ah-queue@example.com',
      dnisName: 'After Hours DNIS',
    },
    {
      lineIndex: 7,
      lineLabel: getMultiLineLabel(8),
      title: 'Billing Dept',
      number: '+1 800 777 7890',
      lineType: CallerIdLineType.HUNT_GROUP,
      isSelected: false,
      serviceUserId: 'hg-billing@example.com',
    },
    {
      lineIndex: 8,
      lineLabel: getMultiLineLabel(9),
      title: 'IT Helpdesk International Support Center',
      number: '+1 800 888 7890',
      lineType: CallerIdLineType.ADDITIONAL_NUMBER,
      isSelected: false,
    },
  ];

  async getLines(): Promise<ICallerIdResponse> {
    await delay(MOCK_DELAY);

    if ((window as any).MOCK_LINE_SELECTION_FAIL_GET) {
      throw new Error('[MockCallerIdService] Simulated GET failure');
    }

    const lines = this.mockLines.map(line => ({
      ...line,
      isSelected: line.lineIndex === this.selectedIndex,
      callerIds: line.callerIds?.map(cid => ({
        ...cid,
        isSelected: line.lineIndex === this.selectedIndex && cid.callerIdIndex === this.selectedCallerIdIndex,
      })),
    }));

    const selected = lines[this.selectedIndex];

    console.log(
      `%c[MockCallerIdService] GET → ${lines.length} lines, selected: ${selected.lineLabel} ${selected.title}` +
        (this.selectedCallerIdIndex !== null ? ` (callerID: ${this.selectedCallerIdIndex})` : ''),
      'color: #36b37e; font-weight: bold'
    );

    return {
      currentSelection: selected.lineType,
      currentSelectionValue: selected.lineType === CallerIdLineType.DEFAULT_CLID
        ? undefined
        : selected.number,
      lines,
    };
  }

  async setLine(line: ILineSelectionInfo): Promise<void> {
    await delay(MOCK_DELAY);

    if ((window as any).MOCK_LINE_SELECTION_FAIL_PUT) {
      throw new Error('[MockCallerIdService] Simulated PUT failure');
    }

    this.selectedIndex = line.lineIndex;
    // Reset caller ID selection when switching lines
    this.selectedCallerIdIndex = line.callerIds ? 0 : null;

    console.log(
      `%c[MockCallerIdService] PUT → selected: ${line.lineLabel} ${line.title} (${line.lineType})`,
      'color: #0065ff; font-weight: bold'
    );
  }
}

function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}
